import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, MapPin, Clock, Briefcase, Calendar, CheckCircle, Send
} from 'lucide-react';
import { cn } from '../../../lib/utils';

// Renders job description: handles Quill HTML, old plain-text-with-tags, or pure plain text
const renderDescription = (desc) => {
    if (!desc) return '<p style="opacity:0.5">No description provided.</p>';
    // If it contains any HTML opening tags, render as HTML
    if (/<[a-zA-Z][\s\S]*?>/.test(desc)) {
        return desc;
    }
    // Plain text fallback — convert newlines to <br>
    return desc.replace(/\n/g, '<br/>');
};

const JobDetailModal = ({ show, onClose, job, onApply, isApplied, appStatus, isApplying }) => {
    if (!job) return null;

    return (
        <AnimatePresence>
            {show && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/70 backdrop-blur-md"
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="w-full max-w-[700px] max-h-[90vh] bg-background border border-border rounded-[2rem] relative overflow-hidden flex flex-col shadow-2xl"
                    >
                        {/* Header */}
                        <div className="p-8 border-b border-border flex justify-between items-start gap-6 bg-accent/20">
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap gap-2 items-center mb-4">
                                    <span className="px-3 py-1 bg-primary/10 text-primary border border-primary/20 rounded-full text-[10px] font-black uppercase tracking-widest">
                                        {job.type}
                                    </span>
                                    {isApplied && (
                                        <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-border" style={{ backgroundColor: appStatus?.bg, color: appStatus?.color, borderColor: appStatus?.color + '40' }}>
                                            {appStatus?.label}
                                        </span>
                                    )}
                                </div>
                                <h2 className="text-2xl md:text-3xl font-black text-foreground tracking-tight m-0 leading-tight">
                                    {job.title}
                                </h2>
                                <p className="text-muted-foreground mt-2 text-base font-bold flex items-center gap-2">
                                    {job.companyName}
                                </p>
                            </div>
                            <motion.button
                                whileHover={{ scale: 1.1, backgroundColor: 'rgba(var(--accent), 0.5)' }}
                                whileTap={{ scale: 0.9 }}
                                onClick={onClose}
                                className="w-10 h-10 rounded-xl bg-background/50 border border-border text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center shrink-0"
                            >
                                <X size={20} />
                            </motion.button>
                        </div>

                        {/* Body */}
                        <div className="p-8 overflow-y-auto flex-1 custom-scrollbar">
                            {/* Info Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                                <div className="flex gap-3 items-center">
                                    <div className="w-10 h-10 rounded-xl bg-accent/30 border border-border/50 flex items-center justify-center text-primary shrink-0">
                                        <MapPin size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Location</div>
                                        <div className="text-sm font-black text-foreground truncate">{job.location}</div>
                                    </div>
                                </div>

                                <div className="flex gap-3 items-center">
                                    <div className="w-10 h-10 rounded-xl bg-accent/30 border border-border/50 flex items-center justify-center text-primary shrink-0">
                                        <span className="text-lg font-black italic">₹</span>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Stipend</div>
                                        <div className="text-sm font-black text-emerald-500 truncate">{job.stipend || 'Not specified'}</div>
                                    </div>
                                </div>

                                <div className="flex gap-3 items-center">
                                    <div className="w-10 h-10 rounded-xl bg-accent/30 border border-border/50 flex items-center justify-center text-primary shrink-0">
                                        <Clock size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Duration</div>
                                        <div className="text-sm font-black text-foreground truncate">{job.duration || 'N/A'}</div>
                                    </div>
                                </div>

                                <div className="flex gap-3 items-center">
                                    <div className="w-10 h-10 rounded-xl bg-accent/30 border border-border/50 flex items-center justify-center text-primary shrink-0">
                                        <Calendar size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Posted</div>
                                        <div className="text-sm font-black text-foreground truncate">
                                            {job.createdAt?.toDate ? job.createdAt.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Recent'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Skills */}
                            {job.skills && (
                                <div className="mb-8">
                                    <h3 className="text-[11px] font-black text-muted-foreground uppercase tracking-[0.2em] mb-4">Required Arsenal</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {(typeof job.skills === 'string' ? job.skills.split(',') : (Array.isArray(job.skills) ? job.skills : [])).map((skill, i) => {
                                            const skillName = typeof skill === 'string' ? skill.trim() : String(skill || '');
                                            if (!skillName) return null;
                                            return (
                                                <span key={i} className="px-4 py-1.5 bg-accent/30 border border-border/50 rounded-full text-[11px] font-bold text-foreground/80 hover:bg-accent/50 transition-colors">
                                                    {skillName}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            <div className="mb-10">
                                <h3 className="text-lg font-black text-foreground mb-4 flex items-center gap-2.5">
                                    <Briefcase size={18} className="text-primary" /> Job Description
                                </h3>
                                <div
                                    className="job-desc-body"
                                    style={{
                                        color: 'var(--muted-foreground)',
                                        fontSize: 15,
                                        lineHeight: 1.8,
                                    }}
                                    dangerouslySetInnerHTML={{ __html: renderDescription(job.description) }}
                                />
                                <style>{`
                                    .job-desc-body p {
                                        margin: 0 0 12px 0;
                                        color: var(--foreground);
                                        opacity: 0.85;
                                    }
                                    .job-desc-body h1, .job-desc-body h2, .job-desc-body h3,
                                    .job-desc-body h4, .job-desc-body h5, .job-desc-body h6 {
                                        color: var(--foreground);
                                        font-weight: 900;
                                        letter-spacing: -0.02em;
                                        line-height: 1.3;
                                        margin: 20px 0 8px 0;
                                    }
                                    .job-desc-body h1 { font-size: 1.4rem; }
                                    .job-desc-body h2 { font-size: 1.2rem; }
                                    .job-desc-body h3 { font-size: 1.05rem; }
                                    .job-desc-body h4, .job-desc-body h5, .job-desc-body h6 { font-size: 0.95rem; }
                                    .job-desc-body strong, .job-desc-body b {
                                        color: var(--foreground);
                                        font-weight: 800;
                                    }
                                    .job-desc-body em, .job-desc-body i {
                                        font-style: italic;
                                        opacity: 0.9;
                                    }
                                    .job-desc-body ul, .job-desc-body ol {
                                        margin: 8px 0 16px 0;
                                        padding-left: 20px;
                                        display: flex;
                                        flex-direction: column;
                                        gap: 6px;
                                    }
                                    .job-desc-body ul { list-style: none; padding-left: 0; }
                                    .job-desc-body ul li {
                                        padding-left: 20px;
                                        position: relative;
                                        color: var(--foreground);
                                        opacity: 0.85;
                                    }
                                    .job-desc-body ul li::before {
                                        content: '▸';
                                        position: absolute;
                                        left: 0;
                                        color: var(--primary);
                                        font-size: 12px;
                                        top: 3px;
                                    }
                                    .job-desc-body ol { list-style: decimal; }
                                    .job-desc-body ol li {
                                        color: var(--foreground);
                                        opacity: 0.85;
                                        padding-left: 4px;
                                    }
                                    .job-desc-body a {
                                        color: var(--primary);
                                        text-decoration: underline;
                                        text-underline-offset: 3px;
                                    }
                                    .job-desc-body hr {
                                        border: none;
                                        border-top: 1px solid var(--border);
                                        margin: 20px 0;
                                    }
                                    .job-desc-body blockquote {
                                        border-left: 3px solid var(--primary);
                                        padding-left: 16px;
                                        margin: 12px 0;
                                        opacity: 0.8;
                                        font-style: italic;
                                    }
                                    .job-desc-body code {
                                        background: var(--accent);
                                        padding: 2px 6px;
                                        border-radius: 6px;
                                        font-size: 13px;
                                        font-family: monospace;
                                    }
                                `}</style>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="p-6 md:p-8 bg-card border-t border-border flex gap-4">
                            <motion.button
                                whileHover={!isApplied ? { scale: 1.02, y: -2 } : {}}
                                whileTap={!isApplied ? { scale: 0.98 } : {}}
                                onClick={() => !isApplied && onApply(job)}
                                disabled={isApplied || isApplying}
                                className={cn(
                                    "flex-1 h-14 rounded-2xl text-sm font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all",
                                    isApplied
                                        ? "bg-accent/30 text-muted-foreground border border-border cursor-default"
                                        : "bg-primary text-primary-foreground shadow-xl shadow-primary/20 hover:bg-primary/90"
                                )}
                            >
                                {isApplying ? (
                                    'Transmitting...'
                                ) : isApplied ? (
                                    <><CheckCircle size={18} /> {appStatus?.label}</>
                                ) : (
                                    <><Send size={16} /> Deploy Application</>
                                )}
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default JobDetailModal;
